// src/pages/PromoManager.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatMainWithSecondary } from "../lib/currency";

const API = process.env.REACT_APP_API_BASE ?? "";

// unified light input style for readability over dark bg
const INPUT_LIGHT =
  "bg-white/90 border border-white/40 text-slate-900 placeholder-slate-500 caret-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-400 rounded-xl";

// helpers
async function getJSON(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { Accept: "application/json", ...(opts.headers || {}) },
    ...opts,
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status} ${path}${t ? ` — ${t}` : ""}`);
  }
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : null;
}
const fmtDate = (s) => (s ? new Date(s).toLocaleString("bg-BG") : "—");

function promoTypeLabel(t) {
  if (t === "percent") return "Процент";
  if (t === "fixed") return "Фиксирана сума (лв.)";
  if (t === "gift") return "Подарък (безплатен бар артикул)";
  if (t === "bundle") return "Пакет (крайна цена за билет + бар)";
  return t;
}

function buildRuleExample(mode, qtyPerTicket, ticketsPerSet, qtyPerSet) {
  const qpt = Math.max(0, Number(qtyPerTicket || 0));
  const tps = Math.max(1, Number(ticketsPerSet || 1));
  const qps = Math.max(0, Number(qtyPerSet || 0));

  if (mode === "per_set") {
    // 1..4 tickets example
    const e1 = 1 < tps ? 0 : Math.floor(1 / tps) * qps;
    const e2 = Math.floor(2 / tps) * qps;
    const e3 = Math.floor(3 / tps) * qps;
    const e4 = Math.floor(4 / tps) * qps;
    return `Пример: 1 билет → ${e1} бр., 2 билета → ${e2} бр., 3 билета → ${e3} бр., 4 билета → ${e4} бр. (всеки ${tps} билета → ${qps} бр.)`;
  }
  // per_ticket
  const e1 = 1 * qpt;
  const e2 = 2 * qpt;
  const e3 = 3 * qpt;
  return `Пример: 1 билет → ${e1} бр., 2 билета → ${e2} бр., 3 билета → ${e3} бр. (по ${qpt} бр. на билет)`;
}

// Component
export default function PromoManager() {
  const navigate = useNavigate();

  const [promos, setPromos] = useState([]);
  const [cinemas, setCinemas] = useState([]);
  const [movies, setMovies] = useState([]);
  const [barArticles, setBarArticles] = useState([]);

  const [q, setQ] = useState(""); // search
  const [statusFilter, setStatusFilter] = useState(""); // all/active/inactive

  // form state (create/edit)
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    code: "",
    name: "",
    type: "percent", // percent | fixed | gift | bundle
    amount: "",
    valid_from: "",
    valid_to: "",
    max_uses: "",
    per_user_limit: "",
    apply_all_cinemas: true,
    apply_all_movies: true,
    cinema_ids: [],
    movie_ids: [],
    bundle_bar_items: [], // [{ article_id, mode, qty_per_ticket, tickets_per_set, qty_per_set }]
    active: true,
    notes: "",
  });

  const editing = !!editId;

  const toast = (m, t = "ok") => {
    setFlash({ t, m });
    clearTimeout(window.__promo_to);
    window.__promo_to = setTimeout(() => setFlash(null), 2600);
  };
  const [flash, setFlash] = useState(null);

  // load base
  useEffect(() => {
    (async () => {
      const [ps, cs, ms] = await Promise.all([
        getJSON("/api/promos").catch(() => []),
        getJSON("/api/cinemas").catch(() => []),
        getJSON("/api/movies").catch(() => []),
      ]);

      setPromos(Array.isArray(ps) ? ps : []);
      setCinemas(Array.isArray(cs) ? cs : []);
      setMovies(Array.isArray(ms) ? ms : []);

      // load bar articles for all cinemas (admin list)
      const cinemasArr = Array.isArray(cs) ? cs : [];
      const allBars = [];

      for (const c of cinemasArr) {
        const cid = c.cinema_id ?? c.CINEMA_ID;
        if (!cid) continue;
        const list = await getJSON(`/api/bar/articles/all/${cid}`).catch(() => []);
        if (Array.isArray(list)) allBars.push(...list);
      }

      setBarArticles(allBars);
    })().catch(console.error);
  }, []);

  const refreshPromos = async () => {
    const ps = await getJSON("/api/promos").catch(() => []);
    setPromos(Array.isArray(ps) ? ps : []);
  };

  const resetForm = () => {
    setEditId(null);
    setForm({
      code: "",
      name: "",
      type: "percent",
      amount: "",
      valid_from: "",
      valid_to: "",
      max_uses: "",
      per_user_limit: "",
      apply_all_cinemas: true,
      apply_all_movies: true,
      cinema_ids: [],
      movie_ids: [],
      bundle_bar_items: [],
      active: true,
      notes: "",
    });
  };

  const startEdit = (p) => {
    setEditId(p.promo_id ?? p.id);
    setForm({
      code: p.code || "",
      name: p.name || "",
      type: p.type || "percent",
      amount: String(p.amount ?? ""),
      valid_from: p.valid_from ? p.valid_from.slice(0, 16) : "",
      valid_to: p.valid_to ? p.valid_to.slice(0, 16) : "",
      max_uses: p.max_uses == null ? "" : String(p.max_uses),
      per_user_limit: p.per_user_limit == null ? "" : String(p.per_user_limit),
      apply_all_cinemas: !!p.apply_all_cinemas,
      apply_all_movies: !!p.apply_all_movies,
      cinema_ids: Array.isArray(p.cinema_ids) ? p.cinema_ids.map(String) : [],
      movie_ids: Array.isArray(p.movie_ids) ? p.movie_ids.map(String) : [],
      bundle_bar_items: Array.isArray(p.bundle_bar_items)
        ? p.bundle_bar_items.map((it) => {
            const aid = String(it.article_id ?? it.ARTICLE_ID);
            const hasSet =
              (it.tickets_per_set != null && Number(it.tickets_per_set) > 0) &&
              (it.qty_per_set != null && Number(it.qty_per_set) > 0);
            return {
              article_id: aid,
              mode: hasSet ? "per_set" : "per_ticket",
              qty_per_ticket: String(it.qty_per_ticket ?? 1),
              tickets_per_set: String(it.tickets_per_set ?? 2),
              qty_per_set: String(it.qty_per_set ?? 1),
            };
          })
        : [],
      active: !!p.active,
      notes: p.notes || "",
    });
  };

  const savePromo = async (e) => {
    e?.preventDefault?.();
    if (!form.code.trim()) return toast("Въведете код.", "err");
    if (!form.name.trim()) return toast("Въведете име.", "err");

    const t = form.type || "percent";
    const amountNum = Number(form.amount || 0);

    // validation per type
    if (t === "percent") {
      if (form.amount === "" || Number.isNaN(amountNum)) return toast("Въведете процент.", "err");
      if (amountNum <= 0 || amountNum > 100) return toast("Процентът трябва да е между 0 и 100.", "err");
    }
    if (t === "fixed") {
      if (form.amount === "" || Number.isNaN(amountNum)) return toast("Въведете сума.", "err");
      if (amountNum <= 0) return toast("Сумата трябва да е положителна.", "err");
    }
    if (t === "bundle") {
      if (form.amount === "" || Number.isNaN(amountNum)) return toast("Въведете цена на пакет.", "err");
      if (amountNum <= 0) return toast("Цената на пакета трябва да е положителна.", "err");
      if (!form.bundle_bar_items.length) return toast("Изберете поне 1 бар артикул за пакета.", "err");
    }
    if (t === "gift") {
      if (!form.bundle_bar_items.length) return toast("Изберете поне 1 бар артикул за подарък.", "err");
    }

    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      type: t,
      amount: t === "gift" ? 0 : Number(form.amount || 0),
      valid_from: form.valid_from ? new Date(form.valid_from).toISOString() : null,
      valid_to: form.valid_to ? new Date(form.valid_to).toISOString() : null,
      max_uses: form.max_uses === "" ? null : Number(form.max_uses),
      per_user_limit: form.per_user_limit === "" ? null : Number(form.per_user_limit),
      apply_all_cinemas: !!form.apply_all_cinemas,
      apply_all_movies: !!form.apply_all_movies,
      cinema_ids: form.apply_all_cinemas ? [] : form.cinema_ids.map(Number),
      movie_ids: form.apply_all_movies ? [] : form.movie_ids.map(Number),
      bundle_bar_items:
        t === "gift" || t === "bundle"
          ? form.bundle_bar_items
              .filter((it) => {
                if (!it.article_id) return false;
                const mode = it.mode || "per_ticket";
                if (mode === "per_set") {
                  return Number(it.tickets_per_set || 0) > 0 && Number(it.qty_per_set || 0) > 0;
                }
                return Number(it.qty_per_ticket || 0) > 0;
              })
              .map((it) => {
                const mode = it.mode || "per_ticket";
                if (mode === "per_set") {
                  return {
                    article_id: Number(it.article_id),
                    tickets_per_set: Number(it.tickets_per_set || 2),
                    qty_per_set: Number(it.qty_per_set || 1),
                  };
                }
                return {
                  article_id: Number(it.article_id),
                  qty_per_ticket: Number(it.qty_per_ticket || 1),
                };
              })
          : [],
      active: !!form.active,
      notes: form.notes.trim(),
    };

    try {
      if (editing) {
        await getJSON(`/api/promos/${editId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        toast("Кампанията е обновена.");
      } else {
        await getJSON("/api/promos", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        toast("Създадена е нова кампания.");
      }
      await refreshPromos();
      resetForm();
    } catch (e2) {
      toast(e2.message || "Грешка при запис.", "err");
    }
  };

  const toggleActive = async (p) => {
    try {
      await getJSON(`/api/promos/${p.promo_id ?? p.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active: !p.active }),
      });
      await refreshPromos();
    } catch {
      toast("Неуспешно сменяне на статус.", "err");
    }
  };

  const delPromo = async (p) => {
    if (!window.confirm(`Изтриване на промо „${p.name || p.code}“?`)) return;
    try {
      await getJSON(`/api/promos/${p.promo_id ?? p.id}`, { method: "DELETE" });
      await refreshPromos();
      toast("Изтрито.");
    } catch {
      toast("Неуспешно изтриване.", "err");
    }
  };

  const filtered = useMemo(() => {
    const text = q.toLowerCase().trim();
    return (promos || []).filter((p) => {
      const okText =
        !text ||
        (p.code || "").toLowerCase().includes(text) ||
        (p.name || "").toLowerCase().includes(text);
      const okStatus =
        !statusFilter ||
        (statusFilter === "active" && p.active) ||
        (statusFilter === "inactive" && !p.active);
      return okText && okStatus;
    });
  }, [promos, q, statusFilter]);

  const toggleInList = (list, id) => {
    id = String(id);
    return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
  };

  const showAmount = form.type !== "gift";
  const showBarSection = form.type === "gift" || form.type === "bundle";

  const visibleBarArticles = useMemo(() => {
    if (form.apply_all_cinemas) return barArticles;
    const allowed = new Set((form.cinema_ids || []).map(String));
    return (barArticles || []).filter((a) => allowed.has(String(a.cinema_id ?? a.CINEMA_ID)));
  }, [barArticles, form.apply_all_cinemas, form.cinema_ids]);

  const barSectionTitle =
    form.type === "gift"
      ? "🎁 Подарък бар артикули (безплатни)"
      : "📦 Артикули в пакета (билет + бар)";

  const barSectionHelp =
    form.type === "gift"
      ? "Как работи: Добавя се автоматично според правилото (на билет / на N билета). На фискалния бон се печата нормалната цена + отрицателен ред (отстъпка), така че устройството да НЕ дава „Грешка при печат“."
      : "Как работи: Избраните артикули се добавят автоматично според правилото. „Цена на пакет“ означава крайна цена за 1 комплект (1 билет + тези артикули). Системата изчислява отстъпката и я печата като отрицателен ред.";

  const amountPlaceholder =
    form.type === "percent"
      ? "% отстъпка"
      : form.type === "fixed"
      ? "Сума (лв.)"
      : form.type === "bundle"
      ? "Крайна цена на пакет (лв.)"
      : "";

  return (
    <div className="min-h-screen relative p-4 md:p-6 [color-scheme:light]">
      {/* Background */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <img
          src="https://latonacinema.com/wp-content/uploads/2025/06/cinema-hall-with-rows-of-grey-seats-in-darkness.jpg"
          alt="фон"
          className="h-full w-full object-cover blur-[2px] scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2c204f]/80 via-black/60 to-[#6c56b5]/70" />
      </div>

      {/* Header */}
      <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-3 mb-4">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 text-white border border-white/20 hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-purple-400/60"
          title="Назад"
        >
          ← Назад
        </button>
        <div className="flex items-center gap-3">
          <img
            src="https://latonacinema.com/wp-content/uploads/2025/06/cropped-latonacinema.png"
            alt="Лого"
            className="h-10"
          />
          <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
            🎟️ Промо кампании — Администрация
          </h1>
        </div>
      </div>

      {/* Card */}
      <div className="max-w-7xl mx-auto bg-white/8 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl p-4 md:p-6 text-white">
        {/* Create / Edit form */}
        <form className="grid gap-3 mb-6" onSubmit={savePromo}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              className={`px-3 py-2 ${INPUT_LIGHT}`}
              placeholder="Код (например POPCORN10)"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
            />
            <input
              className={`px-3 py-2 ${INPUT_LIGHT}`}
              placeholder="Име на кампанията"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />

            <div className="flex items-center gap-2">
              <select
                className={`px-3 py-2 ${INPUT_LIGHT}`}
                value={form.type}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((f) => ({
                    ...f,
                    type: v,
                    // if switching away from gift/bundle, clear bar items to avoid confusion
                    bundle_bar_items: v === "gift" || v === "bundle" ? f.bundle_bar_items : [],
                    // gift doesn't need amount
                    amount: v === "gift" ? "" : f.amount,
                  }));
                }}
              >
                <option value="percent">Процент</option>
                <option value="fixed">Фиксирана сума (лв.)</option>
                <option value="gift">Подарък (безплатен бар артикул)</option>
                <option value="bundle">Пакет (крайна цена за билет + бар)</option>
              </select>

              <input
                type="number"
                step="0.01"
                className={`flex-1 px-3 py-2 ${INPUT_LIGHT} ${showAmount ? "" : "opacity-60 cursor-not-allowed"}`}
                placeholder={showAmount ? amountPlaceholder : "Не се използва при „Подарък“"}
                value={form.amount}
                disabled={!showAmount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              />
            </div>
          </div>

          <div className="text-xs text-white/80 bg-white/10 border border-white/20 rounded-xl px-3 py-2">
            <div className="font-semibold mb-1">Какво означава типът промо?</div>
            <ul className="list-disc pl-5 space-y-0.5">
              <li><b>Подарък</b>: добавя избраните бар артикули и ги прави безплатни (печата отстъпка като отрицателен ред).</li>
              <li><b>Пакет</b>: „Цена на пакет“ е крайната цена за 1 билет + избраните бар артикули (системата изчислява отстъпката).</li>
              <li><b>Процент/Фиксирана</b>: прави отстъпка върху билети (и/или според POS логиката), без автоматични бар артикули.</li>
            </ul>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              type="datetime-local"
              className={`px-3 py-2 ${INPUT_LIGHT}`}
              value={form.valid_from}
              onChange={(e) => setForm((f) => ({ ...f, valid_from: e.target.value }))}
            />
            <input
              type="datetime-local"
              className={`px-3 py-2 ${INPUT_LIGHT}`}
              value={form.valid_to}
              onChange={(e) => setForm((f) => ({ ...f, valid_to: e.target.value }))}
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                className={`px-3 py-2 ${INPUT_LIGHT}`}
                placeholder="Макс. използвания (празно = без лимит)"
                value={form.max_uses}
                onChange={(e) => setForm((f) => ({ ...f, max_uses: e.target.value }))}
              />
              <input
                type="number"
                className={`px-3 py-2 ${INPUT_LIGHT}`}
                placeholder="На потребител (празно = без лимит)"
                value={form.per_user_limit}
                onChange={(e) => setForm((f) => ({ ...f, per_user_limit: e.target.value }))}
              />
            </div>
          </div>

          {/* Scope */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Cinemas */}
            <div className="rounded-2xl border border-white/15 bg-white/5 p-3">
              <div className="font-semibold mb-2">Обхват по кино</div>
              <label className="inline-flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={form.apply_all_cinemas}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      apply_all_cinemas: e.target.checked,
                      cinema_ids: e.target.checked ? [] : f.cinema_ids,
                    }))
                  }
                />
                <span>За всички кина</span>
              </label>
              {!form.apply_all_cinemas && (
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {cinemas.map((c) => {
                    const id = String(c.cinema_id ?? c.CINEMA_ID);
                    const checked = form.cinema_ids.includes(id);
                    return (
                      <label
                        key={id}
                        className="inline-flex items-center gap-2 bg-white/10 rounded-lg px-2 py-1 border border-white/20"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            setForm((f) => ({ ...f, cinema_ids: toggleInList(f.cinema_ids, id) }))
                          }
                        />
                        <span>{c.cinema_name ?? c.CINEMA_NAME ?? c.name ?? "Кино"}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              <div className="text-xs text-white/70 mt-2">
                Ако махнете „За всички кина“ → промото става <b>кино-базирано</b>.
              </div>
            </div>

            {/* Movies */}
            <div className="rounded-2xl border border-white/15 bg-white/5 p-3">
              <div className="font-semibold mb-2">Обхват по филм</div>
              <label className="inline-flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={form.apply_all_movies}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      apply_all_movies: e.target.checked,
                      movie_ids: e.target.checked ? [] : f.movie_ids,
                    }))
                  }
                />
                <span>За всички филми</span>
              </label>
              {!form.apply_all_movies && (
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-auto pr-1">
                  {movies
                    .slice()
                    .sort((a, b) => (a.MOVIE_NAME || "").localeCompare(b.MOVIE_NAME || "", "bg"))
                    .map((m) => {
                      const id = String(m.MOVIE_ID ?? m.movie_id);
                      const checked = form.movie_ids.includes(id);
                      return (
                        <label
                          key={id}
                          className="inline-flex items-center gap-2 bg-white/10 rounded-lg px-2 py-1 border border-white/20"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() =>
                              setForm((f) => ({ ...f, movie_ids: toggleInList(f.movie_ids, id) }))
                            }
                          />
                          <span className="truncate">{m.MOVIE_NAME}</span>
                        </label>
                      );
                    })}
                </div>
              )}
              <div className="text-xs text-white/70 mt-2">
                Ако махнете „За всички филми“ → промото става <b>филм-базирано</b>.
              </div>
            </div>
          </div>

          {/* Bar section only for gift/bundle */}
          {showBarSection && (
            <div className="mt-2 rounded-2xl border border-white/15 bg-white/5 p-3">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                <div className="font-semibold">{barSectionTitle}</div>
                <div className="text-xs text-white/80 sm:max-w-[55%]">{barSectionHelp}</div>
              </div>

              {form.type === "bundle" && (
                <div className="text-xs text-white/80 bg-white/10 border border-white/20 rounded-xl px-3 py-2 mb-2">
                  <b>Важно:</b> „Крайна цена на пакет“ е за <b>1 билет + избраните артикули</b>. Ако касиер избере 2 места → ще има 2 пакета.
                </div>
              )}

              {!visibleBarArticles.length && (
                <div className="text-sm text-white/70">
                  Няма заредени бар артикули (провери /api/bar/articles/all/:cinema_id).
                </div>
              )}

              {!!visibleBarArticles.length && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-auto pr-1">
                  {visibleBarArticles.map((a) => {
                    const id = String(a.article_id ?? a.ARTICLE_ID);
                    const selected = form.bundle_bar_items.find((it) => it.article_id === id) || null;

                    return (
                      <div
                        key={`${id}-${a.cinema_id ?? a.CINEMA_ID ?? "c"}`}
                        className="flex flex-col gap-1 rounded-lg bg-white/10 border border-white/20 px-2 py-1"
                      >
                        <label className="inline-flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={!!selected}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setForm((f) => {
                                let list = [...f.bundle_bar_items];
                                const idx = list.findIndex((it) => it.article_id === id);
                                if (checked && idx === -1) {
                                  list.push({
                                    article_id: id,
                                    mode: "per_ticket",
                                    qty_per_ticket: "1",
                                    tickets_per_set: "2",
                                    qty_per_set: "1",
                                  });
                                } else if (!checked && idx !== -1) {
                                  list.splice(idx, 1);
                                }
                                return { ...f, bundle_bar_items: list };
                              });
                            }}
                          />
                          <span className="truncate">
                            {a.article_name}{" "}
                            <span className="text-xs text-white/60">
                              ({formatMainWithSecondary(Number(a.price))}, кино {a.cinema_id})
                            </span>
                          </span>
                        </label>

                        {selected && (
                          <div className="flex flex-col gap-1 text-xs">
                            <div className="flex items-center gap-2">
                              <span className="min-w-[120px]">Правило:</span>
                              <select
                                className={`px-2 py-1 text-xs ${INPUT_LIGHT}`}
                                value={selected.mode || "per_ticket"}
                                onChange={(e) => {
                                  const mode = e.target.value;
                                  setForm((f) => ({
                                    ...f,
                                    bundle_bar_items: f.bundle_bar_items.map((it) =>
                                      it.article_id === id ? { ...it, mode } : it
                                    ),
                                  }));
                                }}
                              >
                                <option value="per_ticket">На билет (за всеки билет)</option>
                                <option value="per_set">На N билета (на всеки N билета)</option>
                              </select>
                            </div>

                            {String(selected.mode || "per_ticket") === "per_set" ? (
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="min-w-[120px]">Всеки</span>
                                <input
                                  type="number"
                                  min="1"
                                  step="1"
                                  className={`w-20 px-2 py-1 text-xs ${INPUT_LIGHT}`}
                                  value={selected.tickets_per_set ?? "2"}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setForm((f) => ({
                                      ...f,
                                      bundle_bar_items: f.bundle_bar_items.map((it) =>
                                        it.article_id === id ? { ...it, tickets_per_set: value } : it
                                      ),
                                    }));
                                  }}
                                />
                                <span>билета →</span>
                                <input
                                  type="number"
                                  min="1"
                                  step="1"
                                  className={`w-20 px-2 py-1 text-xs ${INPUT_LIGHT}`}
                                  value={selected.qty_per_set ?? "1"}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setForm((f) => ({
                                      ...f,
                                      bundle_bar_items: f.bundle_bar_items.map((it) =>
                                        it.article_id === id ? { ...it, qty_per_set: value } : it
                                      ),
                                    }));
                                  }}
                                />
                                <span>бр.</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className="min-w-[120px]">бр. на билет:</span>
                                <input
                                  type="number"
                                  min="1"
                                  step="1"
                                  className={`w-20 px-2 py-1 text-xs ${INPUT_LIGHT}`}
                                  value={selected.qty_per_ticket ?? "1"}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setForm((f) => ({
                                      ...f,
                                      bundle_bar_items: f.bundle_bar_items.map((it) =>
                                        it.article_id === id ? { ...it, qty_per_ticket: value } : it
                                      ),
                                    }));
                                  }}
                                />
                              </div>
                            )}

                            <div className="text-[11px] text-white/75 bg-white/10 border border-white/15 rounded-lg px-2 py-1">
                              {buildRuleExample(
                                selected.mode || "per_ticket",
                                selected.qty_per_ticket,
                                selected.tickets_per_set,
                                selected.qty_per_set
                              )}
                            </div>

                            {form.type === "bundle" && (
                              <div className="text-[11px] text-white/75">
                                * При тип <b>Пакет</b>: бар артикулите се добавят и се изчислява отстъпка така, че комплектът да стане{" "}
                                <b>{form.amount ? formatMainWithSecondary(Number(form.amount)) : "…"} </b>
                                (за 1 билет).
                              </div>
                            )}

                            {form.type === "gift" && (
                              <div className="text-[11px] text-white/75">
                                * При тип <b>Подарък</b>: тези артикули стават <b>безплатни</b> (печата се отстъпка -100% като отрицателен ред).
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/20 text-white border border-white/20">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
              />
              Активна кампания
            </label>
            <input
              className={`px-3 py-2 md:col-span-2 ${INPUT_LIGHT}`}
              placeholder="Бележка (вътрешна информация)"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button type="submit" className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white">
              {editing ? "💾 Запази" : "➕ Създай"}
            </button>
            {editing && (
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 rounded-xl bg-white/10 text-white border border-white/20"
              >
                Отказ
              </button>
            )}
          </div>
        </form>

        {/* Toolbar */}
        <div className="sticky top-2 z-10 mb-3 rounded-xl bg-white/10 border border-white/20 backdrop-blur px-3 py-2 flex flex-wrap items-center gap-2">
          <input
            className={`px-3 py-2 ${INPUT_LIGHT}`}
            placeholder="Търси по код или име…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <select
            className={`px-3 py-2 ${INPUT_LIGHT}`}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Всички</option>
            <option value="active">Активни</option>
            <option value="inactive">Неактивни</option>
          </select>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-2xl border border-white/15 bg-white/5">
          <table className="min-w-full text-sm">
            <thead className="bg-white/10 text-white">
              <tr>
                <th className="p-2 border border-white/10">Код</th>
                <th className="p-2 border border-white/10">Име</th>
                <th className="p-2 border border-white/10">Тип / Стойност</th>
                <th className="p-2 border border-white/10">Валидност</th>
                <th className="p-2 border border-white/10">Лимити</th>
                <th className="p-2 border border-white/10">Обхват</th>
                <th className="p-2 border border-white/10">Статус</th>
                <th className="p-2 border border-white/10">Действия</th>
              </tr>
            </thead>
            <tbody className="text-white">
              {!filtered.length && (
                <tr>
                  <td className="p-3 border border-white/10" colSpan={8}>
                    Няма кампании.
                  </td>
                </tr>
              )}
              {filtered.map((p) => {
                let amount;
                if (p.type === "percent") amount = `${p.amount}%`;
                else if (p.type === "fixed") amount = `${formatMainWithSecondary(Number(p.amount))}`;
                else if (p.type === "bundle") amount = `${formatMainWithSecondary(Number(p.amount))} пакет`;
                else if (p.type === "gift") amount = `Подарък`;
                else amount = `${formatMainWithSecondary(Number(p.amount))}`;

                const scope =
                  (p.apply_all_cinemas ? "Всички кина" : "Подбрани кина") +
                  " / " +
                  (p.apply_all_movies ? "Всички филми" : "Подбрани филми");

                return (
                  <tr key={p.promo_id ?? p.id} className="even:bg-white/5">
                    <td className="p-2 border border-white/10 font-semibold">
                      <div className="inline-flex items-center gap-2">
                        <span>{p.code}</span>
                        <button
                          className="px-2 py-0.5 rounded bg-white/10 text-white border border-white/20 text-xs"
                          onClick={() => navigator.clipboard.writeText(p.code)}
                          title="Копирай кода"
                        >
                          Копирай
                        </button>
                      </div>
                    </td>
                    <td className="p-2 border border-white/10">{p.name}</td>
                    <td className="p-2 border border-white/10">
                      <div className="text-xs text-white/80">{promoTypeLabel(p.type)}</div>
                      <div>{amount}</div>
                    </td>
                    <td className="p-2 border border-white/10">
                      <div>
                        {p.valid_from ? fmtDate(p.valid_from) : "—"} → {p.valid_to ? fmtDate(p.valid_to) : "—"}
                      </div>
                    </td>
                    <td className="p-2 border border-white/10">
                      <div className="text-xs">
                        Макс.: {p.max_uses ?? "—"} / Потребител: {p.per_user_limit ?? "—"}
                      </div>
                    </td>
                    <td className="p-2 border border-white/10">{scope}</td>
                    <td className="p-2 border border-white/10">
                      <button
                        onClick={() => toggleActive(p)}
                        className={`px-2 py-1 rounded-lg border ${
                          p.active ? "bg-emerald-600 text-white border-emerald-500" : "bg-white/10 text-white border-white/20"
                        }`}
                      >
                        {p.active ? "Активна" : "Неактивна"}
                      </button>
                    </td>
                    <td className="p-2 border border-white/10">
                      <div className="flex flex-wrap gap-2">
                        <button className="px-3 py-1.5 rounded-lg bg-blue-600 text-white" onClick={() => startEdit(p)}>
                          Редакция
                        </button>
                        <button className="px-3 py-1.5 rounded-lg bg-rose-600 text-white" onClick={() => delPromo(p)}>
                          Изтрий
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Toast */}
      {flash && (
        <div
          className={`fixed bottom-4 right-4 px-4 py-3 rounded-xl shadow-2xl text-white ${
            flash.t === "ok" ? "bg-emerald-600" : flash.t === "err" ? "bg-rose-600" : "bg-slate-700"
          }`}
        >
          {flash.m}
        </div>
      )}
    </div>
  );
}
